<form [formGroup]="playBuilderForm" (ngSubmit)="onSave()">
    <div class="base-criteria-container">
        <div class="base-criteria-instructions-container">
            <r1-compass-lib-instructions headerText="Template Criteria">
                First, select from the list of existing Template Criteria to use. The criteria will then display below.
                Changes to the Template Criteria cannot be made here and must be made in the Template Criteria
                configuration section of the app.
            </r1-compass-lib-instructions>
        </div>
        <div class="base-criteria-dropdown-container">
            <mat-form-field class="base-criteria-dropdown" subscriptSizing="dynamic">
                <mat-label>Select a Template Criteria</mat-label>
                <mat-select
                    [formControl]="playBuilderForm.controls.templateCriteriaId"
                    (selectionChange)="onTemplateCriteriaSelection($event.value)">
                    @if (selectedInvalidTemplateCriteria(); as invalidTemplateCriteria) {
                        <mat-option [value]="invalidTemplateCriteria.id" disabled>
                            {{ invalidTemplateCriteria.name }} (invalid)
                        </mat-option>
                    }
                    @for (template of templates(); track $index) {
                        <mat-option [value]="template.id">{{ template.name }}</mat-option>
                    }
                </mat-select>
                @if (playBuilderForm.controls.templateCriteriaId.hasError('invalidTemplateCriteria')) {
                    <mat-error
                        >This play uses invalid template criteria. Select valid template criteria to
                        continue.</mat-error
                    >
                }
            </mat-form-field>
            @if (playBuilderForm.controls.templateCriteriaId.value) {
                <div class="template-criteria-route-button">
                    <button
                        type="button"
                        mat-stroked-button
                        (click)="routeToTemplateCriteria()"
                        class="rule-link-button">
                        View Template Criteria
                    </button>
                </div>
            }
        </div>
        <div class="template-criteria-description">
            <r1-compass-lib-instructions headerText="">
                {{ templateDescription }}
            </r1-compass-lib-instructions>
        </div>
        <div class="base-criteria-list-container">
            @for (templateCriteriaForm of templateCriteriaFormArray.controls; track $index) {
                @if (templateCriteriaForm.controls.type.value === 'generic') {
                    <r1-compass-app-generic-selection-criterion
                        [index]="$index"
                        [displayIndex]="false"
                        [tables]="genericCriteriaTables()"
                        [criterionForm]="templateCriteriaForm.controls.data | r1NarrowWideFormTypes"
                        [deletable]="false"
                        [displayOnly]="true"
                        [required]="false"
                        (deleteCriterion)="onDeleteCriterion($event)" />
                } @else if (templateCriteriaForm.controls.type.value === 'model') {
                    <r1-compass-app-generic-selection-criterion
                        [index]="$index"
                        [displayIndex]="false"
                        [tables]="modelCriteriaTables()"
                        [criterionForm]="templateCriteriaForm.controls.data | r1NarrowWideFormTypes"
                        [deletable]="false"
                        [displayOnly]="true"
                        [required]="false"
                        (deleteCriterion)="onDeleteCriterion($event)" />
                } @else if (templateCriteriaForm.controls.type.value === 'web search') {
                    <r1-compass-app-web-search-criterion
                        [criterionForm]="templateCriteriaForm.controls.data | r1NarrowWideFormTypes"
                        [index]="$index"
                        [displayIndex]="false"
                        [deletable]="false"
                        [displayOnly]="true"
                        [required]="false"
                        (deleteCriterion)="onDeleteCriterion($event)" />
                } @else if (templateCriteriaForm.controls.type.value === 'web activity') {
                    <r1-compass-app-web-activity-criterion
                        [criterionForm]="templateCriteriaForm.controls.data | r1NarrowWideFormTypes"
                        [index]="$index"
                        [displayIndex]="false"
                        [deletable]="false"
                        [displayOnly]="true"
                        [required]="false"
                        [cmtLevels]="webActivityCmtLevels()"
                        [webActivityPeriodFields]="webActivityPeriodFields()"
                        (deleteCriterion)="onDeleteCriterion($event)" />
                } @else if (templateCriteriaForm.controls.type.value === 'sales history') {
                    <r1-compass-app-sales-history-criterion
                        [criterionForm]="templateCriteriaForm.controls.data | r1NarrowWideFormTypes"
                        [index]="$index"
                        [displayIndex]="false"
                        [deletable]="false"
                        [displayOnly]="true"
                        [required]="false"
                        [cmtLevels]="salesHistoryCmtLevels()"
                        [selfManufacturedOptions]="selfManufacturedOptions()"
                        [sspOptions]="sspOptions()"
                        [privateLabelOptions]="privateLabelOptions()"
                        [channels]="salesHistoryChannels()"
                        [metrics]="salesHistoryMetricFields()"
                        (deleteCriterion)="onDeleteCriterion($event)" />
                }
            }
        </div>
    </div>

    <r1-compass-lib-divider />

    <div class="play-selection-container">
        <div class="play-selection-instructions-container">
            <r1-compass-lib-instructions headerText="Play Selection Criteria">
                Set the desired selection criteria by choosing the database field, comparison logic and match value to
                narrow down your record selection. The wizard allows you to match using both basic (e.g., a single match
                field) and complex query logic (e.g., combining various criteria). To add additional criteria, simply
                click the "Add" button. To remove a criteria, click the (-) icon.
            </r1-compass-lib-instructions>
        </div>
        <div class="selection-criteria-list-container" cdkDropList (cdkDropListDropped)="drop($event)">
            @for (criterionFormGroup of playBuilderForm.controls.selectionCriteria.controls; track $index) {
                <div
                    class="drag-item-container"
                    cdkDrag
                    cdkDragBoundary=".selection-criteria-list-container"
                    cdkDragLockAxis="y">
                    @if (criterionFormGroup.controls.type.value === 'generic') {
                        <r1-compass-app-generic-selection-criterion
                            [index]="$index"
                            [tables]="genericCriteriaTables()"
                            [required]="false"
                            [criterionForm]="criterionFormGroup.controls.data | r1NarrowWideFormTypes"
                            (deleteCriterion)="onDeleteCriterion($event)" />
                    } @else if (criterionFormGroup.controls.type.value === 'model') {
                        <r1-compass-app-generic-selection-criterion
                            [index]="$index"
                            [tables]="modelCriteriaTables()"
                            [criterionForm]="criterionFormGroup.controls.data | r1NarrowWideFormTypes"
                            [required]="false"
                            (deleteCriterion)="onDeleteCriterion($event)" />
                    } @else if (criterionFormGroup.controls.type.value === 'web search') {
                        <r1-compass-app-web-search-criterion
                            [criterionForm]="criterionFormGroup.controls.data | r1NarrowWideFormTypes"
                            [index]="$index"
                            [required]="false"
                            (deleteCriterion)="onDeleteCriterion($event)" />
                    } @else if (criterionFormGroup.controls.type.value === 'web activity') {
                        <r1-compass-app-web-activity-criterion
                            [criterionForm]="criterionFormGroup.controls.data | r1NarrowWideFormTypes"
                            [index]="$index"
                            [required]="false"
                            [cmtLevels]="webActivityCmtLevels()"
                            [webActivityPeriodFields]="webActivityPeriodFields()"
                            (deleteCriterion)="onDeleteCriterion($event)" />
                    } @else if (criterionFormGroup.controls.type.value === 'sales history') {
                        <r1-compass-app-sales-history-criterion
                            [criterionForm]="criterionFormGroup.controls.data | r1NarrowWideFormTypes"
                            [index]="$index"
                            [required]="false"
                            [cmtLevels]="salesHistoryCmtLevels()"
                            [sspOptions]="sspOptions()"
                            [selfManufacturedOptions]="selfManufacturedOptions()"
                            [privateLabelOptions]="privateLabelOptions()"
                            [channels]="salesHistoryChannels()"
                            [metrics]="salesHistoryMetricFields()"
                            (deleteCriterion)="onDeleteCriterion($event)" />
                    }
                </div>
            }
        </div>
        <div class="play-selection-footer">
            <button mat-raised-button color="primary" [matMenuTriggerFor]="menu" type="button">+Add</button>
            <mat-menu #menu="matMenu">
                @if (allowedSelectionCriteria().includes('Generic Criteria')) {
                    <button mat-menu-item (click)="onAddCriteria('generic')" type="button">Generic</button>
                }
                @if (allowedSelectionCriteria().includes('Model Criteria')) {
                    <button mat-menu-item (click)="onAddCriteria('model')" type="button">Model</button>
                }
                @if (allowedSelectionCriteria().includes('Sales History Criteria')) {
                    <button mat-menu-item (click)="onAddCriteria('sales history')" type="button">Sales History</button>
                }
                @if (allowedSelectionCriteria().includes('Web Activity Criteria')) {
                    <button mat-menu-item (click)="onAddCriteria('web activity')" type="button">Web Activity</button>
                }
                @if (allowedSelectionCriteria().includes('Web Search Criteria')) {
                    <button mat-menu-item (click)="onAddCriteria('web search')" type="button">Web Search</button>
                }
            </mat-menu>
        </div>
    </div>

    <r1-compass-lib-divider />
    <r1-compass-lib-grouping-logic-complex [groupingLogicForm]="playBuilderForm.controls.groupingLogic" />
    <r1-compass-lib-divider />

    <div class="test-query-container">
        <div class="test-query-instructions-container">
            <r1-compass-lib-instructions headerText="Test Query">
                To run a test query using the selection criteria above, click the "Run Test Query" button. To override
                the query, first complete the Talking Points, inclusive of any Library Content. Then, enable the
                Segmentation Override toggle and edit the query as needed.
            </r1-compass-lib-instructions>
        </div>
        <div class="test-query-action-container">
            <div class="run-test-query-row">
                <div class="query-button-container">
                    <button mat-raised-button color="primary" (click)="onRunQuery()" type="button">
                        Run Test Query
                    </button>
                </div>
                @if (hasQueryError()) {
                    <div id="http-errors">
                        @if (queryApiError(); as apiError) {
                            <r1-compass-lib-http-error-base [data]="apiError" />
                        }
                        @if (queryDatamartError(); as datamartError) {
                            <r1-compass-lib-http-error-base [data]="datamartError" />
                        }
                    </div>
                } @else {
                    <div class="query-container">
                        <p>
                            <strong>Query:</strong>
                            {{ multipleRecommendationsEnabled() ? multipleRecommendationsQuery : standardQuery }}
                        </p>

                        <p><strong>Create Count:</strong> {{ createCount }}</p>
                        <p><strong>Update Count:</strong> {{ updateCount }}</p>
                    </div>
                }
            </div>
            @if (allowOverrideQueries()) {
                <div class="segmentation-override-row">
                    <div class="override-toggle-container">
                        <mat-slide-toggle
                            [formControl]="overrideToggleControl"
                            (change)="onOverrideQueryToggled($event)"
                            >Segmentation Override
                        </mat-slide-toggle>
                    </div>
                    @if (overrideToggleControl.value) {
                        @if (overrideApiError(); as overrideError) {
                            <div id="http-errors">
                                <r1-compass-lib-http-error-base [data]="overrideError" />
                            </div>
                        } @else {
                            <div class="override-query-container">
                                @if (!multipleRecommendationsEnabled()) {
                                    <mat-form-field>
                                        <mat-label>Query</mat-label>
                                        <textarea
                                            class="override-query-input"
                                            matInput
                                            placeholder="Query"
                                            [formControl]="playBuilderForm.controls.overrideStandardQuery"></textarea>
                                    </mat-form-field>
                                } @else {
                                    <mat-form-field>
                                        <mat-label>Multiple Recommendation</mat-label>
                                        <textarea
                                            class="override-query-input"
                                            matInput
                                            placeholder="Multiple Recommendations"
                                            [formControl]="
                                                playBuilderForm.controls.overrideMultipleRecommendationsQuery
                                            "></textarea>
                                    </mat-form-field>
                                }
                            </div>
                        }
                    }
                </div>
            }
        </div>
    </div>
    <div class="main-footer-container">
        <button mat-raised-button color="primary" [disabled]="disableSave()" type="submit">Save</button>
    </div>
</form>
